import { useCallback, useEffect } from "react";
import { IShop, Shop } from "../../../interfaces/Shop";
import { IOrder, Order } from "../../../interfaces/Order";
import { Project } from "../../../interfaces/Project";
import { useDispatch, useSelector } from "react-redux";
import {
  getPublicViewTemplateDropdown,
  getPublicViewTemplateOptions,
} from "../../../selectors/dropdowns";
import { getGalleryOrderByParent } from "../../../selectors";
import { createLoadOrder } from "../../../actions/order";
import { resetPublicViewTemplateData } from "../../../actions/shop";
import { get } from "lodash";
import {
  parseOrderToPublicViewEntity,
  parseShopToPublicViewEntity,
} from "../../../hooks/usePublicViewEntity";
import {
  CUSTOMIZATION_SIDEBAR_WIDTH,
  getThemesByPublicViewTemplates,
} from "./helpers";
import { useThemeData } from "../../../hooks/useThemeData";
import { useLayoutUpdated } from "../../../hooks/useLayoutUpdated";
import { useIframe } from "../../../hooks/useIFrame";
import {
  Col,
  Csku,
  GlobalStyle,
  Row,
  Theme,
  themeOptions,
} from "@commonsku/styles";
import CartProvider from "../../../context/CartProvider";
import PublicViewTemplateDataProvider from "../../../context/PublicViewTemplateDataProvider";
import PublicViewEntityProvider from "../../../context/PublicViewEntityProvider";
import React from "react";
import { ResponsivePreview } from "../../ResponsivePreview";
import CustomizationsSidebar from "./CustomizationsSidebar";
import createPopup from "../../../popup-factory";
import Overlay from "../../Overlay";
import { additionalGlobalStyle } from "./styles";
import { useScreenSize } from "../../../hooks/useScreenSize";
import StorefrontHeader from "./StorefrontHeader";
import StorefrontPopups from "./StorefrontPopups";
import { usePopup } from "../../../hooks/usePopup";
import { SelectedThemeAndTemplateData } from "../popups/SelectTemplatePopup/TemplateTabs";

interface Popup {
  component: string;
  key?: string;
  [key: string]: any;
}

interface StorefrontProps {
  shop?: Shop;
  order?: Order;
  project?: Project;
  popups?: Popup[];
  order_type?: string;
}

const Storefront: React.FC<StorefrontProps> = (props) => {
  const { shop, order, project, popups } = props;
  const dispatch = useDispatch();
  const { screenSize, setScreenSize, isMobileView } = useScreenSize();
  const { showPopup, popUpConfig, handleShowPopup, setShowPopup } = usePopup();
  const [isIframeLoading, setIsIframeLoading] = React.useState(true);

  const entityType: string = shop ? "SHOP" : "ORDER";
  const entityId: string = shop ? shop?.shop_id : order?.order_id;
  const publicViewTemplateList = useSelector(getPublicViewTemplateDropdown);
  const publicViewTemplate = useSelector(getPublicViewTemplateOptions);
  const gallery = useSelector((state) =>
    getGalleryOrderByParent(state, {
      parent_type: entityType,
      parent_id: entityId,
    })
  );

  const entityValue = shop
    ? parseShopToPublicViewEntity(shop as IShop, gallery)
    : parseOrderToPublicViewEntity(gallery);

  const layouts = getThemesByPublicViewTemplates(
    entityValue.entity,
    publicViewTemplateList
  );
  const selectedTheme: SelectedThemeAndTemplateData = layouts.find(
    (v) => v.public_view_template_id === entityValue.entityLayoutId
  );
  const selectedTemplate: SelectedThemeAndTemplateData = layouts.find(
    (v) => v.public_view_template_id === entityValue.entityTemplateId
  );
  const selectedLayout = selectedTemplate || selectedTheme;
  const themeName = get(selectedTheme, ["public_view_template_name"]);

  useEffect(() => {
    const { loaded, order_id } = gallery;
    if (!loaded && order_id) {
      dispatch(createLoadOrder(order_id));
    }
  }, []);

  useEffect(() => {
    setIsIframeLoading(true);
  }, [entityValue.entityTemplateId, entityValue.entityLayoutId]);

  const handleIframeLoad = useCallback(() => {
    setIsIframeLoading(false);
  }, []);

  const onDiscardPublicViewTemplateChanges = (
    parent_id: string,
    parent_type: string,
    reload_order_id: string
  ) => {
    dispatch(resetPublicViewTemplateData(parent_id, parent_type));
    if (reload_order_id) {
      dispatch(createLoadOrder(reload_order_id, true));
    }
  };

  const templateData = useThemeData(themeName, selectedTheme, selectedTemplate);
  const isLayoutUpdated = useLayoutUpdated(
    entityValue,
    templateData,
    selectedTheme,
    selectedTemplate
  );
  const { customizationRef, sendIframeFilterChange } = useIframe(
    entityValue.entity as IShop | IOrder
  );

  const getHeaderContainerHeight = useCallback(() => {
    const headerContainer = document.querySelector(".header-container");
    return headerContainer?.clientHeight || 315;
  }, []);
  const template_color =
    publicViewTemplate && publicViewTemplate.templateData?.template_color;
  const newThemeOptions = { ...themeOptions, template_color };
  const selectedLayoutThemeName = selectedLayout?.public_view_template_name;
  const selectedThemeType = selectedLayout?.template_type;
  const headerContainerHeight = getHeaderContainerHeight();

  return (
    <Theme theme={newThemeOptions}>
      <GlobalStyle
        additionalStyles={additionalGlobalStyle}
        theme={newThemeOptions}
      />
      <CartProvider
        entityId={entityValue.entityId}
        isEntityFree={+entityValue.entityIsFree}
        allowMultipleFreeItem={
          +entityValue.entityIsFree === 1 &&
          entityValue.freeItemsCount > 1 &&
          entityValue.allowMultipleFreeItem
        }
      >
        <PublicViewTemplateDataProvider initialValue={selectedTheme}>
          <PublicViewEntityProvider initialValue={entityValue}>
            <StorefrontPopups
              showPopup={showPopup}
              setShowPopup={setShowPopup}
              layouts={layouts}
              templateData={templateData}
              selectedTheme={selectedTheme}
              selectedTemplate={selectedTemplate}
              themeName={themeName}
              popUpConfig={popUpConfig}
              entityId={entityValue.entityId}
              entityLayoutId={entityValue.entityLayoutId}
              entityType={entityValue.entityType}
              orderId={entityValue.order?.order_id}
              onDiscardPublicViewTemplateChanges={
                onDiscardPublicViewTemplateChanges
              }
            />
            <div
              className={"main-content shop shop-v3"}
              style={{ background: "var(--color-neutrals-20)" }}
            >
              <Row>
                <Csku
                  forceStyles
                  className="storefront-left-preview"
                  style={{ padding: 14 }}
                  sx={{
                    xs: { width: "100%" },
                    sm: {
                      width: `calc(100% - ${CUSTOMIZATION_SIDEBAR_WIDTH}px)`,
                    },
                  }}
                >
                  <Row>
                    <StorefrontHeader
                      selectedLayoutThemeName={selectedLayoutThemeName}
                      selectedThemeType={selectedThemeType}
                      isLayoutUpdated={isLayoutUpdated}
                      entityTemplateId={entityValue?.entityTemplateId}
                      screenSize={screenSize}
                      setScreenSize={setScreenSize}
                      isMobileView={isMobileView}
                      handleShowPopup={handleShowPopup}
                    />
                    <Col
                      xs
                      style={{
                        textAlign: "center",
                        ...(isMobileView ? { padding: 24 } : {}),
                      }}
                    >
                      <ResponsivePreview
                        id="storefront-template-iframe"
                        display={isMobileView ? "mobile" : "desktop"}
                        src={`${
                          window.location.origin
                        }/${entityValue.entityType.toLowerCase()}/${
                          entityValue.entityId
                        }?hideHelp=true&previewShop=true`}
                        title={entityValue.title}
                        allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        containerStyle={{
                          height: `calc(100vh - ${headerContainerHeight}px - ${
                            isMobileView ? 148 : 100
                          }px)`,
                        }}
                        style={{
                          ...(isMobileView
                            ? {
                                boxShadow: "rgb(220 216 216) 1px 3px 6px 2px",
                              }
                            : {}),
                        }}
                        inset={isMobileView ? 8 : 0}
                        isLoading={isIframeLoading}
                        onLoad={handleIframeLoad}
                      />
                    </Col>
                  </Row>
                </Csku>
                <Csku
                  forceStyles
                  className="storefront-right-sidebar"
                  sx={{
                    xs: { width: "100%" },
                    sm: { width: CUSTOMIZATION_SIDEBAR_WIDTH },
                  }}
                >
                  <CustomizationsSidebar
                    themeName={themeName}
                    selectedTheme={selectedLayout}
                    handleShowPopup={handleShowPopup}
                    onCategoryPageChange={sendIframeFilterChange}
                    ref={customizationRef}
                    isMobileView={isMobileView}
                  />
                </Csku>
              </Row>
            </div>
            {popups
              ? popups.map((p, idx) =>
                  createPopup(p, idx, { project: project })
                )
              : null}
            <Overlay popups={popups || []} />
          </PublicViewEntityProvider>
        </PublicViewTemplateDataProvider>
      </CartProvider>
    </Theme>
  );
};

export default Storefront;
